/**
 * PartnerScriptRunner
 *
 * Executes DB-backed per-partner adapter scripts inside the host page.
 *
 * Backend flow:
 *   1. On session start (after flavor is applied), backend sends
 *      `{ t: 'partner_bundle', partner_id, platform,
 *           adapters: { action: {...} }, autoRun: { action: {...} } }`.
 *      Adapters are object-keyed because action is a unique key within a
 *      partner's bundle — the DB constraint enforces it, the voice LLM's
 *      tool schema assumes it.
 *   2. When the LLM calls e.g. add_to_cart, backend emits a client_tool_call:
 *        toolName: 'run_partner_script'
 *        parameters: { action: 'cart.add', args: { sku, qty } }
 *
 * Compile-on-load: each adapter is compiled into an AsyncFunction immediately
 * when the bundle lands. Syntax errors are isolated (poison markers) — one
 * bad adapter does not brick the bundle, and the error shows up in the
 * console when it can still be fixed, not at first call time in whichever
 * session happens to hit it.
 *
 * Adapter contract (stored in ttp.partner_adapters.code_js):
 *   async (ctx) => { ...; return { ok: true, ... }; }
 *
 * ctx provided to every adapter:
 *   - args         : parameters from the LLM
 *   - host         : location.hostname
 *   - platform     : bundle-level platform ('hybris' | 'shopify' | ...)
 *   - fetch        : bound window.fetch
 *   - log          : console.log with an [adapter:<action>] prefix
 *   - refreshUI    : platform-aware UI nudge
 *   - normalizeCart: raw-cart → canonical cart (platform-dispatched)
 *   - runAdapter   : (action, args) → Promise<result>  (invokes another adapter)
 *   - emit         : (msg) → void  — send an unsolicited message back to the backend
 *
 * Orchestration (DB columns before_actions / after_actions on each adapter row):
 *   runAdapter(action) runs, in order:
 *     1. each before_actions[] entry (full runAdapter — hooks can chain)
 *     2. code_js body (category event + adapter fn)
 *     3. each after_actions[] entry when the body returned ok !== false
 *        (args include _parentResult with the body return value)
 *   before-hook failures are logged and do not block the main body.
 *
 * Inline pre/post hooks (entry-level pre_code_js / post_code_js):
 *   Some bundles (notably the reserved `__client_tools__` client-tool bundle)
 *   ship a 3-step entry: an optional `pre_code_js`, the required `code_js`
 *   (main), and an optional `post_code_js`. These compile alongside `code_js`
 *   and run synchronously in order around the main body:
 *     - pre is best-effort: an error is logged and main still runs;
 *     - main's return value is the result sent back; a main error → ok:false;
 *     - post runs only if main succeeded (mirrors after-hook gating); post
 *       errors are logged best-effort.
 *   A bad pre/post poisons only its own entry (same isolation as code_js).
 *
 * Combined ordering when an entry has both named hooks and inline hooks:
 *     named before_actions → inline pre → main → inline post → named after_actions
 *
 * Category-based event suppression:
 *   When an adapter entry has a non-null `category` field, runAdapter fires a
 *   CustomEvent on window named `netcost:partner-action:{category}` BEFORE
 *   executing the adapter. Subscribers (e.g. cart.subscribe) listen for this
 *   event to suppress their own duplicate notifications.
 */

const LOG = '[PartnerScriptRunner]';

/** Max nested runAdapter depth (before/after hooks calling other hooked actions). */
const MAX_ORCHESTRATION_DEPTH = 16;

function adapterResultOk(result) {
  if (!result || typeof result !== 'object') return true;
  if (!('ok' in result)) return true;
  return !!result.ok;
}

/**
 * Best-effort IDs from run_partner_script args for diagnostics (cart.add, Shopify, etc.).
 * @param {Record<string, unknown>} raw
 * @returns {string[]}
 */
function extractProductIdsFromPartnerArgs(raw) {
  if (!raw || typeof raw !== 'object') return [];
  const out = [];
  const push = (v) => {
    if (v == null || v === '') return;
    const s = String(v);
    if (s) out.push(s);
  };
  push(raw.productId);
  push(raw.sku);
  push(raw.retailerProductId);
  push(raw.variantId);
  if (Array.isArray(raw.items)) {
    for (const i of raw.items) {
      if (!i || typeof i !== 'object') continue;
      push(i.productId);
      push(i.sku);
      push(i.retailerProductId);
      push(i.variantId);
    }
  }
  return [...new Set(out)];
}

// ---------------------------------------------------------------------------
// Compilation
// ---------------------------------------------------------------------------

const AsyncFunction = Object.getPrototypeOf(async function () {}).constructor;

// Wrap and compile an adapter body. Two authoring styles are accepted:
//
//   1. Function expression (how DB partner adapters are stored):
//        async (ctx) => { ...; return {...}; }
//   2. Raw statement body (how client-tool scripts are typed in the UI):
//        alert("hi"); return { ok: true };
//      The body runs inside an AsyncFunction whose single parameter is `ctx`,
//      so `ctx.args` etc. are directly in scope.
//
// Expression style is detected by shape (starts with `async`, `function`, `(`,
// or `name =>`) and tried first; if it doesn't parse as an expression we fall
// back to compiling the code as a plain body. AsyncFunction (not
// `new Function`) enforces an async execution context — no accidental sync
// adapter bodies.
const FN_EXPR_RE = /^\s*(async[\s(])|^\s*function\b|^\s*\(|^\s*[A-Za-z_$][\w$]*\s*=>/;

function compileAdapter(codeJs) {
  if (FN_EXPR_RE.test(codeJs)) {
    try {
      // eslint-disable-next-line no-new-func
      return new AsyncFunction('ctx', `"use strict"; return (${codeJs}).call(null, ctx);`);
    } catch (e) {
      // Looked like an expression but doesn't parse as one (e.g. an
      // IIFE-opening statement body) — fall through to body compilation.
    }
  }
  // eslint-disable-next-line no-new-func
  return new AsyncFunction('ctx', `"use strict"; ${codeJs}`);
}

// Cheap 32-bit string hash, rendered as 12 hex chars. NOT cryptographic —
// good enough for "did the bundle change between pushes?" diagnostics.
function shortHash(s) {
  let h = 0x811c9dc5; // FNV-like seed
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0').repeat(2).slice(0, 12);
}

// ---------------------------------------------------------------------------
// Cart normalization — canonical shape derived in the widget, never passed
// through from the raw platform response. Keeps the LLM looking at clean truth.
// ---------------------------------------------------------------------------

function emptyCart() {
  return { itemCount: 0, lineCount: 0, total: 0, currency: null, totalFormatted: null, entries: [] };
}

function normalizeHybrisCart(raw) {
  if (!raw || typeof raw !== 'object') return emptyCart();
  const rawEntries = Array.isArray(raw.entries) ? raw.entries : [];

  const entries = rawEntries.map((e) => {
    const product = e.product || {};
    const quantity = Number.isFinite(e.quantity) ? e.quantity : (+e.quantity || 0);

    const unitBefore   = e.basePrice?.value ?? product.price?.value ?? null;
    const unitDiscount = e.discountPrice?.value ?? null;
    const unitEffective = unitDiscount != null ? unitDiscount : unitBefore;
    const onSale       = unitDiscount != null && unitBefore != null && unitDiscount < unitBefore;

    const lineTotal = e.totalPrice?.value
                   ?? (unitEffective != null ? +(unitEffective * quantity).toFixed(2) : null);
    const lineTotalBefore = onSale && unitBefore != null
                   ? +(unitBefore * quantity).toFixed(2)
                   : null;

    return {
      sku:              e.productCode ?? product.code ?? null,
      ean:              e.ean ?? product.ean ?? null,
      name:             e.productName ?? product.name ?? product.title ?? null,
      brand:            e.brand ?? product.brand ?? null,
      quantity,
      unitPrice:        unitEffective,
      unitPriceBefore:  onSale ? unitBefore : null,
      lineTotal,
      lineTotalBefore,
      onSale,
      imageUrl:         e.imageUrl ?? product.desktopImage?.url ?? product.image?.url ?? null,
      productUrl:       e.productUrl ?? product.url ?? null,
      maxQuantity:      e.maxOrderQuantity ?? null,
      entryId:          e.entryId ?? null,
    };
  });

  const lineCount = entries.length;
  const itemCount = entries.reduce((n, x) => n + (x.quantity || 0), 0);
  const total = raw.totalPrice?.value
             ?? raw.subTotal?.value
             ?? entries.reduce((s, x) => s + (x.lineTotal || 0), 0);
  const currency = raw.totalPrice?.currencyIso ?? raw.currencyIso ?? null;
  const totalFormatted = raw.totalPrice?.formattedValue
                      ?? (total != null && currency ? `${currency} ${total.toFixed(2)}` : null);

  return { itemCount, lineCount, total, currency, totalFormatted, entries };
}

function normalizeCartByPlatform(platform, raw) {
  switch ((platform || '').toLowerCase()) {
    case 'hybris': return normalizeHybrisCart(raw);
    // shopify / woocommerce / custom: stubs until we onboard them. Hybris
    // shape is the closest thing to canonical, so default to it.
    default:       return normalizeHybrisCart(raw);
  }
}

// Best-effort UI nudge after a cart mutation. No-ops when the platform's
// globals aren't present, so adapters can always call ctx.refreshUI().
function refreshUIForPlatform(platform) {
  return () => {
    try {
      if ((platform || '').toLowerCase() === 'hybris') {
        if (window.minicart?.updateCart) window.minicart.updateCart(false);
      }
    } catch (e) {
      console.warn(LOG, 'refreshUI failed:', e);
    }
  };
}

// ---------------------------------------------------------------------------
// Runner
// ---------------------------------------------------------------------------

export class PartnerScriptRunner {
  /**
   * @param {{ genericCtx?: boolean }} [opts]
   *   genericCtx: when true, ctx omits the ecommerce-specific helpers
   *   (platform / normalizeCart / refreshUI). Used by the flavor-independent
   *   `__client_tools__` runner. Default false preserves the ecommerce ctx.
   */
  constructor(opts = {}) {
    this.genericCtx = !!(opts && opts.genericCtx);
    /** @type {Map<string, {fn?: Function, version?: number, codeSha256?: string, meta?: object, category?: string|null, beforeActions?: string[], afterActions?: string[], poisoned?: boolean, error?: string}>} */
    this.adapters = new Map();
    this.partnerId = null;
    this.platform  = null;
    this.bundleVersion = 0; // monotonic, bumped on every setBundle
    /** @type {((msg: object) => void) | null} */
    this._emitCallback = null;
  }

  /**
   * Register the callback that adapters can use to send unsolicited messages
   * back to the backend via ctx.emit(msg). Called by EcommerceFlavor after it
   * loads the bundle so the callback is always fresh for the active SDK instance.
   * @param {(msg: object) => void} fn
   */
  setEmitCallback(fn) {
    this._emitCallback = typeof fn === 'function' ? fn : null;
  }

  /**
   * Replace the cached bundle from a `partner_bundle` message.
   * @param {{partner_id?: string, platform?: string|null, adapters?: Record<string,{version?:number,code_js:string,code_sha256?:string,meta?:object,before_actions?:string[],after_actions?:string[]}>}} msg
   */
  setBundle(msg) {
    const adapters = msg?.adapters || {};
    this.partnerId = msg?.partner_id || null;
    this.platform  = msg?.platform || null;
    this.adapters  = new Map();

    let compiled = 0;
    let poisoned = 0;

    for (const [action, entry] of Object.entries(adapters)) {
      if (!entry || typeof entry.code_js !== 'string') {
        console.warn(LOG, `skipping malformed adapter '${action}':`, entry);
        this.adapters.set(action, {
          poisoned: true,
          error: 'missing code_js',
          version: entry?.version ?? null,
        });
        poisoned++;
        continue;
      }
      try {
        const fn = compileAdapter(entry.code_js);
        // Inline pre/post hooks (optional). Compiled with the same
        // AsyncFunction strict-mode wrapper as the main body so a syntax
        // error in a hook poisons only this entry (caught below).
        const preFn  = typeof entry.pre_code_js === 'string'  ? compileAdapter(entry.pre_code_js)  : null;
        const postFn = typeof entry.post_code_js === 'string' ? compileAdapter(entry.post_code_js) : null;
        this.adapters.set(action, {
          fn,
          preFn,
          postFn,
          version:    entry.version ?? null,
          codeSha256: entry.code_sha256 || null,
          category:   entry.category ?? null,
          meta:       entry.meta || {},
          beforeActions: Array.isArray(entry.before_actions) ? entry.before_actions : [],
          afterActions:  Array.isArray(entry.after_actions) ? entry.after_actions : [],
        });
        compiled++;
      } catch (err) {
        console.error(LOG, `compile failed for '${action}':`, err);
        this.adapters.set(action, {
          poisoned:  true,
          error:     String(err?.message || err),
          version:   entry.version ?? null,
          category:  entry.category ?? null,
        });
        poisoned++;
      }
    }

    this.bundleVersion += 1;
    const sha = shortHash(JSON.stringify(adapters));
    console.log(
      `${LOG} bundle loaded partner=${this.partnerId} platform=${this.platform} ` +
      `actions=${this.adapters.size} (ok=${compiled} poisoned=${poisoned}) ` +
      `sha=${sha} v${this.bundleVersion}`
    );
    if (poisoned > 0) {
      const bad = [...this.adapters.entries()]
        .filter(([, v]) => v.poisoned)
        .map(([a, v]) => `${a}: ${v.error}`);
      console.warn(`${LOG} poisoned actions:`, bad);
    }
  }

  has(action) { return this.adapters.has(action); }
  get(action) { return this.adapters.get(action); }

  /**
   * Execute an adapter by action name. Used both by the run_partner_script
   * tool handler and by ctx.runAdapter for intra-bundle chaining.
   *
   * When the adapter row defines before_actions / after_actions (from
   * ttp.partner_adapters), those run automatically around code_js.
   *
   * If the adapter has a non-null `category`, fires a window CustomEvent
   * named `netcost:partner-action:{category}` BEFORE executing code_js.
   * Subscribers (e.g. cart.subscribe) use this to suppress their own
   * duplicate notifications for the same mutation.
   *
   * @param {string} action
   * @param {Record<string, unknown>} [args]
   * @param {number} [depth] internal recursion guard
   */
  async runAdapter(action, args = {}, depth = 0) {
    if (depth > MAX_ORCHESTRATION_DEPTH) {
      console.error(`${LOG} orchestration depth exceeded for '${action}' (max ${MAX_ORCHESTRATION_DEPTH})`);
      return { ok: false, reason: 'orchestration_depth_exceeded', action, depth };
    }

    const entry = this.adapters.get(action);
    if (!entry) {
      return { ok: false, reason: 'adapter_not_in_bundle', action };
    }
    if (entry.poisoned) {
      return { ok: false, reason: 'adapter_compile_error', action, detail: entry.error };
    }

    const safeArgs = args && typeof args === 'object' ? args : {};
    const beforeActions = entry.beforeActions || [];
    const afterActions = entry.afterActions || [];

    for (const hookAction of beforeActions) {
      if (!hookAction || typeof hookAction !== 'string') continue;
      console.log(`${LOG} before hook ${action} → ${hookAction}`);
      const hookResult = await this.runAdapter(hookAction, safeArgs, depth + 1);
      if (!adapterResultOk(hookResult)) {
        console.warn(
          `${LOG} before hook '${hookAction}' for '${action}' returned ok=false — continuing with main body:`,
          hookResult,
        );
      }
    }

    const result = await this._runAdapterBody(action, safeArgs, entry);

    if (adapterResultOk(result) && afterActions.length > 0) {
      const afterArgs = { ...safeArgs, _parentResult: result };
      for (const hookAction of afterActions) {
        if (!hookAction || typeof hookAction !== 'string') continue;
        console.log(`${LOG} after hook ${action} → ${hookAction}`);
        const hookResult = await this.runAdapter(hookAction, afterArgs, depth + 1);
        if (!adapterResultOk(hookResult)) {
          console.warn(
            `${LOG} after hook '${hookAction}' for '${action}' returned ok=false:`,
            hookResult,
          );
        }
      }
    }

    return result;
  }

  /**
   * Run code_js only — category event + compiled fn. No before/after hooks.
   * @private
   */
  async _runAdapterBody(action, args, entry) {
    // Fire category event so subscribers can suppress duplicate handling.
    if (entry.category) {
      const eventName = `netcost:partner-action:${entry.category}`;
      const safeArgs = args || {};
      const productIds = extractProductIdsFromPartnerArgs(safeArgs);
      try {
        window.dispatchEvent(new CustomEvent(eventName, {
          detail: { action, args: safeArgs },
        }));
        const idStr = productIds.length ? productIds.join(',') : '(none)';
        console.log(
          `${LOG} dispatched ${eventName} action=${action} productIds=${idStr}`,
          { action, category: entry.category, productIds, productId: productIds[0] ?? null },
        );
      } catch (e) {
        const idStr = productIds.length ? productIds.join(',') : '(none)';
        console.warn(
          `${LOG} failed to dispatch category event '${eventName}' for '${action}' productIds=${idStr}:`,
          e,
        );
      }
    }

    const ctx = this._buildCtx(args, action, entry);
    console.log(
      `${LOG} exec ${action} v${entry.version ?? '?'} ` +
      `sha=${(entry.codeSha256 || '').slice(0, 12)}`
    );

    // Inline pre-hook (best-effort): an error is logged and main still runs.
    if (entry.preFn) {
      try {
        await entry.preFn(ctx);
      } catch (e) {
        console.warn(`${LOG} ${action} inline pre-hook threw (continuing with main body):`, e);
      }
    }

    let result;
    try {
      result = await entry.fn(ctx);
    } catch (e) {
      console.error(`${LOG} ${action} threw:`, e);
      return { ok: false, reason: 'adapter_threw', error: String(e?.message || e) };
    }

    // Inline post-hook runs only when main succeeded (mirror of after-hook
    // gating). Best-effort: errors are logged, the main result is still returned.
    if (entry.postFn && adapterResultOk(result)) {
      const postCtx = this._buildCtx({ ...(args || {}), _mainResult: result }, action, entry);
      try {
        await entry.postFn(postCtx);
      } catch (e) {
        console.warn(`${LOG} ${action} inline post-hook threw (ignored):`, e);
      }
    }

    return result;
  }

  _buildCtx(args, action /*, entry */) {
    const platform = this.platform;
    const emitCallback = this._emitCallback;
    // Send an unsolicited message back to the backend (e.g. cart_change_event).
    // No-op if no emitCallback is registered.
    const emit = (msg) => {
      if (typeof emitCallback === 'function') {
        emitCallback(msg);
      } else {
        console.warn(`${LOG} [adapter:${action}] ctx.emit called but no emitCallback registered`);
      }
    };
    const ctx = {
      args:       args || {},
      host:       window.location.hostname,
      fetch:      window.fetch.bind(window),
      log:        (...a) => console.log(`[adapter:${action}]`, ...a),
      runAdapter: (a, subArgs) => this.runAdapter(a, subArgs),
      emit,
    };
    // Ecommerce-specific helpers are omitted for the flavor-independent
    // `__client_tools__` runner (genericCtx). Client-tool scripts get a
    // platform-agnostic ctx with no cart normalization or UI nudges.
    if (!this.genericCtx) {
      ctx.platform      = platform;
      ctx.refreshUI     = refreshUIForPlatform(platform);
      ctx.normalizeCart = (raw) => normalizeCartByPlatform(platform, raw);
    }
    return ctx;
  }
}

// ---------------------------------------------------------------------------
// Client-tool handler
// ---------------------------------------------------------------------------

/**
 * Build the { name, handler } entry consumed by BaseFlavor.buildTools().
 * Backend sends: parameters = { action, args, ... }
 */
export function buildRunPartnerScriptTool(runner) {
  return {
    name: 'run_partner_script',
    handler: async (parameters = {}) => {
      const { action, args } = parameters;
      if (!action) {
        return { ok: false, reason: 'missing_action' };
      }
      return runner.runAdapter(action, args || {});
    },
  };
}
